import React, { useState } from 'react';
import styled from 'styled-components';
import { useInventory } from '../../contexts/InventoryContext';
import toast from 'react-hot-toast';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: 30px;
  border-radius: 15px;
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(79, 172, 254, 0.5);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
`;

const ModalTitle = styled.h3`
  color: white;
  margin: 0;
  font-size: 1.5rem;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  color: white;
  cursor: pointer;
  font-size: 1.2rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }
  
  &:focus {
    outline: none;
    border-color: #4ade80;
    box-shadow: 0 0 0 2px rgba(74, 222, 128, 0.2);
  }
`;

const Select = styled.select`
  padding: 12px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #4ade80;
    box-shadow: 0 0 0 2px rgba(74, 222, 128, 0.2);
  }
  
  option {
    background: #1e293b;
    color: white;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 10px;
`;

const Button = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
`;

const CancelButton = styled(Button)`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const SubmitButton = styled(Button)`
  background: #4ade80;
  color: white;
  
  &:hover {
    background: #22c55e;
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AddInventoryModal = ({ isOpen, onClose }) => {
  const { addInventoryItem } = useInventory();
  const [formData, setFormData] = useState({
    name: '',
    type: 'precious-metal',
    quantity: '',
    unit: 'oz',
    value: '',
    location: '',
    warehouse: '',
    quality: 'A+'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await addInventoryItem({
        ...formData,
        quantity: parseFloat(formData.quantity),
        value: parseFloat(formData.value),
        id: `INV-${Date.now()}`,
        dateAdded: new Date().toISOString(),
        status: 'active'
      });
      
      // Reset form
      setFormData({
        name: '',
        type: 'precious-metal',
        quantity: '',
        unit: 'oz',
        value: '',
        location: '',
        warehouse: '',
        quality: 'A+'
      });
      
      onClose();
    } catch (error) {
      console.error('Failed to add inventory item:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Add New Inventory Item</ModalTitle>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>
        
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Item Name</Label>
            <Input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g., Gold Bars 999.9"
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Commodity Type</Label>
            <Select
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              required
            >
              <option value="precious-metal">Precious Metal</option>
              <option value="agricultural">Agricultural</option>
              <option value="energy">Energy</option>
              <option value="industrial">Industrial Metal</option>
            </Select>
          </FormGroup>
          
          <FormGroup>
            <Label>Quantity</Label>
            <Input
              type="number"
              name="quantity"
              value={formData.quantity}
              onChange={handleInputChange}
              placeholder="Enter quantity"
              step="0.01"
              min="0"
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Unit</Label>
            <Select
              name="unit"
              value={formData.unit}
              onChange={handleInputChange}
              required
            >
              <option value="oz">Ounces (oz)</option>
              <option value="kg">Kilograms (kg)</option>
              <option value="lbs">Pounds (lbs)</option>
              <option value="tons">Tons</option>
              <option value="barrels">Barrels</option>
              <option value="bushels">Bushels</option>
            </Select>
          </FormGroup>
          
          <FormGroup>
            <Label>Market Value (USD)</Label>
            <Input
              type="number"
              name="value"
              value={formData.value}
              onChange={handleInputChange}
              placeholder="Enter market value"
              step="0.01"
              min="0"
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Location</Label>
            <Input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              placeholder="e.g., Vault A, Section 1"
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Warehouse</Label>
            <Select
              name="warehouse"
              value={formData.warehouse}
              onChange={handleInputChange}
              required
            >
              <option value="">Select Warehouse</option>
              <option value="WH001">Central Warehouse</option>
              <option value="WH002">East Coast Storage</option>
              <option value="WH003">West Coast Facility</option>
            </Select>
          </FormGroup>
          
          <FormGroup>
            <Label>Quality Grade</Label>
            <Select
              name="quality"
              value={formData.quality}
              onChange={handleInputChange}
              required
            >
              <option value="A+">A+ (Premium)</option>
              <option value="A">A (High)</option>
              <option value="B+">B+ (Good)</option>
              <option value="B">B (Standard)</option>
              <option value="C">C (Below Standard)</option>
            </Select>
          </FormGroup>
          
          <ButtonGroup>
            <CancelButton type="button" onClick={onClose}>
              Cancel
            </CancelButton>
            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Adding...' : 'Add Item'}
            </SubmitButton>
          </ButtonGroup>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AddInventoryModal; 